import isEqual from "lodash/isEqual";
import React, { Suspense, useState, useEffect, useMemo } from "react";

import { VStack, Box } from "@whoosh/design-system/src/components/primitive";

import { LoadingSpinner } from "../../display/Loader/LoadingSpinner";
import { Menu } from "../../overlay/Menu";
import { MenuContentSize } from "../../overlay/Menu/types";
import { Trigger } from "../Trigger/Trigger";
import { Group } from "./components/Group";
import { OptionalToggle } from "./components/OptionalToggle/OptionalToggle";

export type DropDownSize = "tiny" | "small" | "regular" | "large";

export type DropdownSelectOption<T> = {
  value: T;
  label: string | React.JSX.Element;
  subLabel?: string;
  isDisabled?: boolean;
  showUnderline?: boolean;
  leftElement?: React.JSX.Element;
  rightElement?: React.JSX.Element;
  triggerElement?: React.JSX.Element;
};

export type DropdownSelectOptionsGroup<T> = {
  label: string;
  options: DropdownSelectOption<T>[];
};

export type DropdownLoadableOptionsComponent<T> = {
  render: LoadableOptionsComponentRenderFunction<T>;
};

export type LoadableOptionsComponentRenderFunction<T> = (
  options: DropdownSelectOptionsGroup<T>[] | DropdownSelectOption<T>[]
) => React.JSX.Element;

export type DropdownSelectProps<T> = {
  size?: DropDownSize;
  placeholder?: string;
  value?: T;
  name?: string;
  options: DropdownSelectOptionsGroup<T>[] | DropdownSelectOption<T>[];
  onChange: (value: T) => void;
  testID?: string;
  ariaLabel?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isViewOnly?: boolean;
  includeLeftElementInSelectedValue?: boolean;
  hideChevron?: boolean;
  hideLabelInDisplay?: boolean;
  hideSubLabelInDisplay?: boolean;
  hideOuterMargin?: boolean;
  onOpen?: () => void;
  loadableOptionsComponent?: (
    render: LoadableOptionsComponentRenderFunction<T>
  ) => React.JSX.Element | null;
  optionalToggle?: {
    labelOverride?: string;
    onToggle: (isChecked: boolean) => void;
    initiallyChecked?: boolean;
  };
  contentSizeOverride?: MenuContentSize;
  showVerticalScrollIndicator?: boolean;
};

export const DropdownSelect = <T extends string | number | null>({
  size = "regular",
  value,
  options,
  onChange,
  optionalToggle,
  placeholder,
  testID,
  ariaLabel,
  hasError,
  isDisabled,
  isViewOnly,
  includeLeftElementInSelectedValue,
  hideOuterMargin = false,
  hideLabelInDisplay = false,
  hideSubLabelInDisplay = false,
  onOpen,
  hideChevron = false,
  loadableOptionsComponent,
  contentSizeOverride,
  showVerticalScrollIndicator = false,
}: DropdownSelectProps<T>) => {
  const isGrouped = options[0] && options[0].hasOwnProperty("options");

  const groupedOptions = useMemo(
    () =>
      isGrouped
        ? (options as DropdownSelectOptionsGroup<T>[])
        : [{ label: "", options: options as DropdownSelectOption<T>[] }],
    [isGrouped, options]
  );

  // Initialize allOptions with groupedOptions
  const [allOptions, setAllOptions] =
    useState<DropdownSelectOptionsGroup<T>[]>(groupedOptions);
  const [loadedOptions, setLoadedOptions] = useState<
    DropdownSelectOptionsGroup<T>[]
  >([]);

  // Merge new options into allOptions when options prop changes
  useEffect(() => {
    setAllOptions(groupedOptions);
  }, [groupedOptions]);

  const selectedItem = useMemo(
    () => getValueIndexesFromString(value, [...allOptions, ...loadedOptions]),
    [value, allOptions, loadedOptions]
  );

  const memoizedLoadableOptions = useMemo(() => {
    if (!loadableOptionsComponent) return null;

    return ({ closeMenu }: { closeMenu: () => void }) => (
      <Suspense
        fallback={
          <Box
            justifyContent="center"
            height={42}
            paddingLeft={3}
            testID="loading-spinner"
          >
            <LoadingSpinner />
          </Box>
        }
      >
        {loadableOptionsComponent((group) => {
          const isGrouped = group[0] && group[0].hasOwnProperty("options");
          const loadedGroupedOptions = isGrouped
            ? (group as DropdownSelectOptionsGroup<T>[])
            : [
                {
                  label: "",
                  options: group as DropdownSelectOption<T>[],
                },
              ];

          // Commit loaded options after render to avoid setState during render
          // This is slightly hacky -- but all it's doing is ensuring we call that
          // state setter OUTSIDE of the render function.
          const LoadedOptionsCommitter = ({
            options,
          }: {
            options: DropdownSelectOptionsGroup<T>[];
          }) => {
            useEffect(() => {
              setLoadedOptions((prev) =>
                isEqual(prev, options) ? prev : options
              );
            }, [options]);
            return null;
          };

          return (
            <>
              <LoadedOptionsCommitter options={loadedGroupedOptions} />
              {loadedGroupedOptions.map((group, i) => (
                <Group<T>
                  key={`loadable-group-${i}`}
                  group={group}
                  selectedItems={selectedItem ? [selectedItem] : []}
                  onItemPress={(value: T) => {
                    onChange(value);
                    closeMenu();
                  }}
                  testID={
                    testID
                      ? `${testID}-loadable-group-${i}`
                      : `dropdown-select-loadable-group-${i}`
                  }
                />
              ))}
            </>
          );
        })}
      </Suspense>
    );
  }, [loadableOptionsComponent, selectedItem, testID, onChange]);

  return (
    <VStack paddingBottom={hideOuterMargin ? 0 : 4} aria-label={ariaLabel}>
      <Menu
        trigger={({ isOpen, onPress }) => {
          return (
            <Trigger
              size={size}
              onPress={() => {
                if (!isDisabled && !isViewOnly) {
                  onPress();
                  onOpen && onOpen();
                }
              }}
              placeholder={placeholder ?? ""}
              testID={testID}
              selectedItem={selectedItem || null}
              isDisabled={isDisabled}
              includeLeftElementInSelectedValue={
                !!includeLeftElementInSelectedValue
              }
              hideLabelInDisplay={hideLabelInDisplay}
              hideSubLabelInDisplay={hideSubLabelInDisplay}
              isViewOnly={isViewOnly}
              hasError={hasError}
              isOpened={isOpen}
              hideChevron={hideChevron}
            />
          );
        }}
        content={({ closeMenu }) => (
          <Menu.Content
            size={contentSizeOverride ?? "matchTrigger"}
            showVerticalScrollIndicator={showVerticalScrollIndicator}
          >
            <>
              {optionalToggle ? <OptionalToggle {...optionalToggle} /> : null}
              {allOptions.map((group, i) => (
                <Group<T>
                  key={`group-${i}`}
                  group={group}
                  selectedItems={selectedItem ? [selectedItem] : []}
                  onItemPress={(value: T) => {
                    onChange(value);
                    closeMenu();
                  }}
                  testID={
                    testID
                      ? `${testID}-group-${i}`
                      : `dropdown-select-group-${i}`
                  }
                />
              ))}
              {memoizedLoadableOptions &&
                memoizedLoadableOptions({ closeMenu })}
            </>
          </Menu.Content>
        )}
      />
    </VStack>
  );
};

const getValueIndexesFromString = <T extends string | number | null>(
  valueString: T | null | undefined,
  groupedOptions: DropdownSelectOptionsGroup<T>[]
): DropdownSelectOption<T> | null => {
  return (
    groupedOptions
      .flatMap((group) => group.options)
      .find((option) => option.value === valueString) || null
  );
};

export { MultiSelectCheckbox } from "./MultiSelectCheckbox";
